import { observer } from 'mobx-react';
import React from 'react';
import { TextField } from '@components/textfield';
import { Badge } from '@components/ui/badge';
import { Button } from '@components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle
} from '@components/ui/card';
import { toast } from '@core/toast';
import { UserContext } from '..';
import { useError } from '../../../components/hooks/useError';
import { Api } from '../../../core/api';
import { UserAppDto } from '../../../core/api/MobileClient';
import { injectTSDI } from '../../../core/tsdi';

type UserAppState = UserAppDto['state'];

const EMPTY_STATE = '{}';

function parseState(text: string): UserAppState | undefined {
    try {
        const parsed = JSON.parse(text.trim() || EMPTY_STATE);
        const isObject =
            typeof parsed === 'object' &&
            parsed !== null &&
            !Array.isArray(parsed);
        return isObject ? parsed : undefined;
    } catch {
        return undefined;
    }
}

const formatState = (state: UserAppState) => JSON.stringify(state, null, 2);

const UserAppRow = ({
    userApp,
    busy,
    onSaveState,
    onDisable
}: {
    userApp: UserAppDto;
    busy: boolean;
    onSaveState(app: string, state: UserAppState): Promise<void>;
    onDisable(app: string): Promise<void>;
}) => {
    const [stateText, setStateText] = React.useState(
        formatState(userApp.state)
    );
    const state = parseState(stateText);
    const changed = stateText !== formatState(userApp.state);

    React.useEffect(() => {
        setStateText(formatState(userApp.state));
    }, [userApp.state]);

    return (
        <div className="flex flex-col gap-3 py-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap items-center gap-3">
                    <span className="font-mono text-sm font-semibold">
                        {userApp.app}
                    </span>
                    <Badge variant="outline">
                        {userApp.userControlled
                            ? 'User controlled'
                            : 'Assigned'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                        Enabled {userApp.enabledDate.slice(0, 10)}
                    </span>
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    disabled={busy}
                    onClick={() => void onDisable(userApp.app)}
                >
                    Disable
                </Button>
            </div>
            <TextField
                type="text"
                multiline
                label="State (JSON)"
                value={stateText}
                error={state ? undefined : 'Not a JSON object'}
                onChange={(value) => setStateText(String(value))}
            />
            <div>
                <Button
                    size="sm"
                    disabled={busy || !state || !changed}
                    onClick={() =>
                        state && void onSaveState(userApp.app, state)
                    }
                >
                    Save state
                </Button>
            </div>
        </div>
    );
};

export const UserAppsCard = observer(() => {
    const user = React.useContext(UserContext);
    const { mobileClient } = injectTSDI(Api);
    const showError = useError();
    const [userApps, setUserApps] = React.useState<UserAppDto[]>([]);
    const [busy, setBusy] = React.useState(false);
    const [newApp, setNewApp] = React.useState('');
    const [newStateText, setNewStateText] = React.useState(EMPTY_STATE);
    const newState = parseState(newStateText);
    const alreadyEnabled = userApps.some(
        (userApp) => userApp.app === newApp.trim()
    );

    const run = async (action: () => Promise<void>) => {
        setBusy(true);
        try {
            await action();
            const { data } =
                await mobileClient.supportApi.userAppSupportControllerGetUserApps(
                    user.id
                );
            setUserApps(data);
        } catch (e) {
            showError(e);
        } finally {
            setBusy(false);
        }
    };

    React.useEffect(() => {
        void run(async () => undefined);
    }, [user.id]);

    const enable = () =>
        run(async () => {
            await mobileClient.supportApi.userAppSupportControllerEnableUserApp(
                user.id,
                newApp.trim(),
                { state: newState! }
            );
            toast.success(`${newApp.trim()} enabled`);
            setNewApp('');
            setNewStateText(EMPTY_STATE);
        });

    const saveState = (app: string, state: UserAppState) =>
        run(async () => {
            await mobileClient.supportApi.userAppSupportControllerSetUserAppState(
                user.id,
                app,
                { state }
            );
            toast.success(`State of ${app} saved`);
        });

    const disable = (app: string) =>
        run(async () => {
            await mobileClient.supportApi.userAppSupportControllerDisableUserApp(
                user.id,
                app
            );
            toast.success(`${app} disabled`);
        });

    return (
        <Card>
            <CardHeader>
                <CardTitle>Apps</CardTitle>
                <CardDescription>
                    Apps enabled for this user. An app enabled here is assigned:
                    the user cannot switch it on or off. The community
                    whitelabel is B2B, with the community and whitelabel in its
                    state.
                </CardDescription>
            </CardHeader>
            <CardContent>
                {userApps.length === 0 ? (
                    <p className="text-sm text-muted-foreground">
                        No app is enabled.
                    </p>
                ) : (
                    <div className="flex flex-col divide-y divide-border">
                        {userApps.map((userApp) => (
                            <UserAppRow
                                key={userApp.app}
                                userApp={userApp}
                                busy={busy}
                                onSaveState={saveState}
                                onDisable={disable}
                            />
                        ))}
                    </div>
                )}
                <div className="mt-6 flex flex-col gap-3 border-t border-border pt-6">
                    <TextField
                        type="text"
                        label="Enable an app"
                        placeholder="B2B"
                        value={newApp}
                        error={
                            alreadyEnabled
                                ? 'Already enabled. Edit its state above.'
                                : undefined
                        }
                        onChange={(value) => setNewApp(String(value))}
                    />
                    <TextField
                        type="text"
                        multiline
                        label="State (JSON)"
                        placeholder='{"whitelabelId": "otto"}'
                        value={newStateText}
                        error={newState ? undefined : 'Not a JSON object'}
                        onChange={(value) => setNewStateText(String(value))}
                    />
                    <div>
                        <Button
                            disabled={
                                busy ||
                                !newApp.trim() ||
                                !newState ||
                                alreadyEnabled
                            }
                            onClick={() => void enable()}
                        >
                            Enable
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
});
